import {
  type DisableAddButtonReason,
  MAX_SESSIONS,
  useSessionFormManagement,
} from '@exam-center/exam-event-management';
import {
  ExamType,
  useCreateExamEventMutation,
  useGetAllExamCenterCoursesQuery,
  useGetAllProvincesQuery,
  useGetAvailableRoomsByLocationIdAndTimeFrameLazyQuery,
  useGetExamCenterLocationsByProvinceIdQuery,
  useSearchAvailableExamCenterProctorsByTimeFrameLazyQuery,
} from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSnackbar } from '@shared/snackbar';
import { dayjs } from '@shared/utils';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { ExamEventCreationForm } from '../../components';
import { EXAM_EVENT_CREATION_ERROR_MESSAGE_MAP } from '../../constants';
import { getExamEventCreationErrorCode } from '../../utils';

import {
  mapExamEventCreationFormInputsToExamEventCreationInput,
  mapExamEventCreationFormInputsToGetAvailableProctorsInput,
  mapExamEventCreationFormInputsToGetAvailableRoomsInput,
} from './mapper';
import { ExamEventCreationFormSchema, type ExamEventCreationFormType } from './schema';
import { getDefaultValues } from './utils';

export interface RoomData {
  id: string;
  name: string;
  seat: number;
  floor: string;
  roomNumber: string | null;
  building: string;
}

interface ExamEventCreationLogicProps {
  examCenterCode: string;
  year: {
    param: string;
    value: number;
  };
  month: {
    param: string;
    value: number;
  };
}

export default function ExamEventCreationLogic(props: Readonly<ExamEventCreationLogicProps>) {
  const {
    examCenterCode,
    year: { param: yearParam, value: year },
    month: { param: monthParam, value: month },
  } = props;

  const { showPromiseSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const formContext = useForm<ExamEventCreationFormType>({
    defaultValues: getDefaultValues(),
    resolver: zodResolver(ExamEventCreationFormSchema),
    mode: 'onSubmit',
  });

  const examType = formContext.watch('examType');

  const { data: examCenterCoursesData, loading: isLoading } = useGetAllExamCenterCoursesQuery({
    variables: {
      getAllExamCenterCoursesInput: {
        examCenterCode,
        sessionCount: examType === ExamType.Paper ? 1 : null,
      },
    },
  });

  const examCourses = formContext.watch('examCourses');

  const examCoursesOptions = useMemo(() => {
    const examCourseOptions = [
      ...(examCenterCoursesData?.ecGetAllExamCenterCourses.examCenterCourses ?? []),
    ]
      .sort((a, b) => a.examCourse.name.localeCompare(b.examCourse.name))
      .map((course) => ({
        label: course.examCourse.name,
        value: course.examCourse.id,
      }));

    if (examCourseOptions.length === 0) {
      return [];
    }

    if (examType === ExamType.Computer) {
      return [
        { label: `เลือกทั้งหมด (${examCourseOptions.length})`, value: 'all' },
        ...examCourseOptions,
      ];
    }

    return examCourseOptions;
  }, [examCenterCoursesData, examType]);

  const [createExamEvent, { loading: isCreating }] = useCreateExamEventMutation({
    onCompleted: (data) => {
      navigate('/:examCenterCode/exam-events/:examEventId', {
        replace: true,
        params: {
          examCenterCode,
          examEventId: data?.ecCreateExamEvent?.examEvent.id ?? '',
        },
      });
    },
  });

  const handleCreateExamEvent = async (data: ExamEventCreationFormType) => {
    const createExamEventResponse = await createExamEvent({
      variables: {
        createExamEventInput: mapExamEventCreationFormInputsToExamEventCreationInput(
          data,
          examCenterCode,
          examCoursesOptions.filter((course) => course.value !== 'all'),
        ),
      },
    });

    if (createExamEventResponse.errors) {
      const errorMessage = Array.isArray(createExamEventResponse.errors)
        ? createExamEventResponse.errors.map((error) => error.message).join(', ')
        : createExamEventResponse.errors;

      return Promise.reject(new Error('Failed to create exam event: ' + errorMessage));
    }

    return Promise.resolve();
  };

  const handleCancel = () => {
    navigate('/:examCenterCode/exam-events/years/:year/months/:month', {
      replace: true,
      params: {
        examCenterCode,
        year: yearParam,
        month: monthParam,
      },
    });
  };

  const handleSubmit = async (data: ExamEventCreationFormType) => {
    showPromiseSnackbar({
      message: {
        pending: 'กำลังสร้างรอบสอบ',
        success: 'สร้างรอบสอบเรียบร้อยแล้ว',
        error: (error: unknown) => {
          const errorCode = getExamEventCreationErrorCode(error);
          if (errorCode && EXAM_EVENT_CREATION_ERROR_MESSAGE_MAP[errorCode]) {
            return EXAM_EVENT_CREATION_ERROR_MESSAGE_MAP[errorCode];
          }

          return 'ไม่สามารถสร้างรอบสอบได้';
        },
      },
      promise: () => handleCreateExamEvent(data),
    });
  };

  const {
    location,
    examDate,
    eventStartDate,
    eventEndDate,
    mainProctors = [],
  } = formContext.watch();

  function isSelected(value: Date | Record<string, string> | null | undefined) {
    return value !== undefined && value !== null;
  }

  const isProvinceSelected = isSelected(location?.province);
  const isNameSelected = isSelected(location?.locationName);
  const isExamCourseSelected = (formContext.getValues('examCourses')?.length ?? 0) > 0;
  const locationRoom = Object.values(location?.room ?? {}).filter((value) => value);
  const isRoomSelected = locationRoom.length > 0;
  const isExamDateSelected =
    isSelected(examDate?.date) && isSelected(examDate?.endTime) && isSelected(examDate?.startTime);

  const isAnyFieldSelected =
    isProvinceSelected ||
    isSelected(eventEndDate) ||
    isSelected(eventStartDate) ||
    mainProctors.length;

  useEffect(() => {
    if (location.room) {
      const selectedRoom = Object.entries(location.room)
        .filter(([, value]) => value)
        .map(([id]) => id);
      if (selectedRoom.length > 0) {
        const totalSeat = selectedRoom.reduce((acc, curr) => {
          const room = roomData.find((r) => r.id === curr);
          return acc + (room?.seat ?? 0);
        }, 0);
        formContext.setValue('location.seat', totalSeat);
      } else if (selectedRoom.length === 0 && isNameSelected) {
        formContext.resetField('location.seat');
      }
    }
  }, [locationRoom.length]);

  useEffect(() => {
    formContext.resetField('location.room');
    formContext.resetField('location.seat');
  }, [location?.locationName]);

  useEffect(() => {
    formContext.resetField('location.locationName');
    formContext.resetField('location.room');
    formContext.resetField('location.seat');
  }, [location?.province]);

  useEffect(() => {
    if (isAnyFieldSelected) {
      const oldValues = formContext.getValues();
      formContext.reset({
        ...oldValues,
        eventStartDate: undefined,
        eventEndDate: undefined,
        location: {
          province: undefined,
          locationName: undefined,
          room: undefined,
          seat: undefined,
        },
        mainProctors: undefined,
      });
    }
  }, [examDate?.date]);

  useEffect(() => {
    if (isAnyFieldSelected) {
      const oldValues = formContext.getValues();
      formContext.reset({
        ...oldValues,
        eventStartDate: undefined,
        eventEndDate: undefined,
        location: {
          province: undefined,
          locationName: undefined,
          room: undefined,
          seat: undefined,
        },
        mainProctors: undefined,
      });
    }
  }, [examDate?.startTime, examDate?.endTime]);

  const { data: getAllProvincesResult, loading: isLoadingProvincesOptions } =
    useGetAllProvincesQuery();

  const provincesOptions = useMemo(() => {
    return (
      getAllProvincesResult?.ecGetAllProvinces.provinces
        .map((province) => ({
          label: province.nameTh,
          value: province.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []
    );
  }, [getAllProvincesResult]);

  const { data: getLocationsResult, loading: isLoadingLocationOptions } =
    useGetExamCenterLocationsByProvinceIdQuery({
      variables: {
        getExamCenterLocationsByProvinceIdInput: {
          examCenterCode,
          provinceId: location?.province?.value ?? '',
        },
      },
      skip: !isProvinceSelected,
    });

  const locationsOptions = useMemo(() => {
    return (
      getLocationsResult?.ecGetExamCenterLocationsByProvinceId.examCenterLocations
        .map((location) => ({
          label: location.location?.nameTh ?? '',
          value: location.location?.id ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []
    );
  }, [getLocationsResult]);

  const [getAvailableRooms, { data: getAvailableRoomsResult, loading: isLoadingAvailableRooms }] =
    useGetAvailableRoomsByLocationIdAndTimeFrameLazyQuery({
      fetchPolicy: 'network-only',
    });

  const roomData = useMemo(() => {
    return (
      getAvailableRoomsResult?.ecGetAvailableRoomsByLocationIdAndTimeFrame.rooms
        .map((room) => ({
          id: room.id,
          name: room.name,
          seat: room.seat,
          floor: room.floor,
          roomNumber: room.roomNumber,
          building: room.building.nameTh,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)) ?? []
    );
  }, [getAvailableRoomsResult]);

  const [
    searchAvailableExamCenterProctorsByTimeFrame,
    { data: getMainProctorResult, loading: isLoadingMainProctorResult },
  ] = useSearchAvailableExamCenterProctorsByTimeFrameLazyQuery({
    fetchPolicy: 'network-only',
  });

  const mainProctorOptions = useMemo(() => {
    return (
      getMainProctorResult?.ecSearchAvailableExamCenterProctorsByTimeFrame.examCenterProctors.map(
        (proctor) => ({
          label: [proctor.titleTh, proctor.firstNameTh, proctor.middleNameTh, proctor.lastNameTh]
            .filter(Boolean)
            .join(' '),
          value: proctor.id,
        }),
      ) ?? []
    );
  }, [getMainProctorResult]);

  useEffect(() => {
    const { unsubscribe } = formContext.watch((value, { name }) => {
      if (name === 'location.locationName') {
        if (value.location?.locationName?.value) {
          getAvailableRooms({
            variables: {
              getAvailableRoomsByLocationIdAndTimeFrameInput:
                mapExamEventCreationFormInputsToGetAvailableRoomsInput(
                  value as ExamEventCreationFormType,
                  examCenterCode,
                ),
            },
          });
        }
      }

      if (
        name === 'examDate.date' ||
        name === 'examDate.startTime' ||
        name === 'examDate.endTime'
      ) {
        if (value.examDate?.date && value.examDate?.startTime && value.examDate?.endTime) {
          searchAvailableExamCenterProctorsByTimeFrame({
            variables: {
              searchAvailableExamCenterProctorsByTimeFrameInput:
                mapExamEventCreationFormInputsToGetAvailableProctorsInput(
                  value as ExamEventCreationFormType,
                  examCenterCode,
                ),
            },
          });
        }
      }
    });

    return () => unsubscribe();
  }, [formContext]);

  useEffect(() => {
    if (examType === ExamType.Paper) {
      formContext.setValue('examCourses', []);
      formContext.setValue('sessions', []);
    }
    if (examType === ExamType.Computer) {
      const allExamCourses =
        examCenterCoursesData?.ecGetAllExamCenterCourses.examCenterCourses ?? [];

      if (allExamCourses.length === 0) {
        formContext.setValue('examCourses', []);
        return;
      }

      formContext.setValue('examCourses', [
        {
          label: `เลือกทั้งหมด (${allExamCourses.length})`,
          value: 'all',
        },
      ]);
    }
  }, [examType, examCenterCoursesData]);

  const selectedMonth = new Date(year, month);
  const minimumExamDate = dayjs(selectedMonth).startOf('month').toDate();
  const maximumExamDate = dayjs(selectedMonth).endOf('month').toDate();

  const handleSetExamCoursesValues = (data: { label: string; value: string }[]) => {
    formContext.setValue('examCourses', data);
  };

  const { sessions, totalDuration, addSession, removeSession } = useSessionFormManagement({
    form: formContext,
  });

  const reason: DisableAddButtonReason | undefined = useMemo(() => {
    if (sessions.length === MAX_SESSIONS) {
      return 'sessionLimitReached';
    }

    if (!examDate?.startTime || !examDate?.endTime) {
      return 'examDateNotFilled';
    }
  }, [sessions, examDate]);

  return (
    <ExamEventCreationForm
      form={formContext}
      isCreating={isCreating}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      slotProps={{
        examCourses: {
          disabled: !examType,
          isLoading,
          examCoursesOptions,
          isExamCourseSelected,
          examCourseSelected: examCourses ?? [],
          setSelected: handleSetExamCoursesValues,
          multiple: examType === ExamType.Computer,
        },
        examDate: {
          isExamDateSelected,
          date: examDate?.date ?? undefined,
          eventStartDate: eventStartDate ?? undefined,
          eventEndDate: eventEndDate ?? undefined,
          minDate: minimumExamDate,
          maxDate: maximumExamDate,
        },
        sessions: {
          data: sessions,
          totalDuration,
          onAddSession: addSession,
          onRemoveSession: removeSession,
          errorMessage: formContext.formState.errors.sessions?.root?.message,
        },
        disableAddButton: {
          shouldDisable: !!reason,
          reason,
          hidden: examType === ExamType.Paper,
        },
        province: {
          isProvinceSelected,
          isProvincesOptionsLoading: isLoadingProvincesOptions,
          provincesOptions,
          isLocationOptionsLoading: isLoadingLocationOptions,
          locationsOptions,
          isGetAvailableRoomsLoading: isLoadingAvailableRooms,
        },
        name: {
          isNameSelected,
        },
        room: {
          roomData,
          isRoomSelected,
          roomSelectionError: formContext.formState.errors.location?.room?.root?.message,
        },
        proctor: {
          mainProctors,
          isLoading: isLoadingMainProctorResult,
          mainProctorOptions,
        },
      }}
    />
  );
}
